import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import classNames from 'classnames';
import {graphql, useMutation, useLazyLoadQuery} from 'react-relay/hooks';
import {ConnectionHandler} from 'relay-runtime';
import {useForm, useWatch} from 'react-hook-form';
import {useAuthorizationState} from '../../../../../contexts/Authorization';
import {graphqlWhoamiNextConditionalQuery} from '../../../../../components/graphql';
import Button from '../../../../../components/button/button';
import {getRecaptchaToken} from '../../../../../utils/recaptcha';
import PollForm from '../../poll-form';
import MediaForm from '../../media-form';
import BarChartSquare01Icon from '../../../../../assets/icons0/bar-chart-square-01.svg';
import Image03Icon from '../../../../../assets/icons0/image-03.svg';
import XIcon from '../../../../../assets/icons/x.svg';

const createCommentMutation = graphql`
    mutation createCommentFormCreateCommentMutation($request: CreateVibePostRequestInput!, $includeIsAuthorized: Boolean!) {
        createVibePost(request: $request) {
            id
            message
            created
            media
            removed
            spam
            likesCount
            likedByCurrentUser @include(if: $includeIsAuthorized)
            stemLikesCount
            stemLikedByCurrentUser @include(if: $includeIsAuthorized)
            viewsCount
            viewedByCurrentUser @include(if: $includeIsAuthorized)
            commentedByCurrentUser @include(if: $includeIsAuthorized)
            quotedByCurrentUser @include(if: $includeIsAuthorized)
            commentsCount
            quotesCount
            sharesCount
            sharedByCurrentUser @include(if: $includeIsAuthorized)
            poll {
                id
                options
                optionVotesCounts
                pollVotesCount
                votedByCurrentUser
                votedOptionIndex
            }
            parent {
                id
                commentsCount
                commentedByCurrentUser
            }
            author {
                id
                vibe {
                    username
                }
            }
        }
    }
`;

const CreateCommentForm = React.memo(({parentPostId, onCommentCreated, isAuthorized, onShowLoginPrompt}) => {
    const authorized = useAuthorizationState();
    const {whoamiNext} = useLazyLoadQuery(graphqlWhoamiNextConditionalQuery, {
        includeWhoamiNext: authorized
    });
    const currentUserId = whoamiNext?.id;
    const intl = useIntl();
    const {register, handleSubmit, formState, reset, control, setFocus, watch, setValue} = useForm({
        defaultValues: {
            message: '',
            pollOptions: [],
            media: []
        }
    });
    const message = useWatch({control, name: 'message'});
    const messageLength = message?.length || 0;
    const maxLength = 512;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [showPoll, setShowPoll] = React.useState(false);
    const [showMedia, setShowMedia] = React.useState(false);
    
    const [createPoll, isCreatingPoll] = useMutation(
        graphql`
            mutation createCommentFormCreatePollMutation($request: CreatePollRequestInput!) {
                createPoll(request: $request) {
                    id
                }
            }
        `
    );
    
    const [createComment, isCreatingComment] = useMutation(createCommentMutation);

    const onSubmit = handleSubmit(async (data) => {
        if (!isAuthorized) {
            onShowLoginPrompt?.();
            return;
        }

        const messageValue = data.message?.trim() || '';
        if (!messageValue || messageValue.length > maxLength) {
            return;
        }
        try {
            let pollId = null;
            
            // Create poll first if poll options exist
            if (data.pollOptions && data.pollOptions.length >= 2) {
                const pollOptionsValues = data.pollOptions
                    .map(opt => opt.value?.trim())
                    .filter(opt => opt && opt.length > 0);
                
                if (pollOptionsValues.length >= 2) {
                    const pollReCaptcha = await getRecaptchaToken();
                    pollId = await new Promise((resolve, reject) => {
                        createPoll({
                            variables: {
                                request: {
                                    options: pollOptionsValues,
                                    reCaptcha: pollReCaptcha
                                }
                            },
                            onCompleted: (result) => {
                                resolve(result.createPoll.id);
                            },
                            onError: (error) => {
                                reject(error);
                            }
                        });
                    });
                }
            }
            
            // Extract media URIs from form data
            const mediaUris = data.media
                ?.map(item => item?.uri)
                .filter(uri => uri && uri.trim())
                || [];
            
            // Create comment with pollId and media
            const reCaptcha = await getRecaptchaToken();
            createComment({
                variables: {
                    request: {message: messageValue, parentId: parentPostId, pollId, media: mediaUris.length > 0 ? mediaUris : null, reCaptcha},
                    includeIsAuthorized: authorized
                },
                updater: (store) => {
                    const newComment = store.getRootField('createVibePost');
                    if (!newComment) return;
                    
                    // Append to comments connection
                    if (parentPostId) {
                        const root = store.getRoot();
                        const postRecord = root.getLinkedRecord('vibePostById', {id: parentPostId});
                        if (postRecord) {
                            const connectionID = ConnectionHandler.getConnectionID(
                                postRecord.getDataID(),
                                'CommentsList_comments'
                            );
                            if (connectionID) {
                                const connection = store.get(connectionID);
                                if (connection) {
                                    const edge = store.create(
                                        `client:newEdge:${newComment.getValue('id')}`,
                                        'VibePostEdge'
                                    );
                                    edge.setLinkedRecord(newComment, 'node');
                                    edge.setValue(`${Date.now()}`, 'cursor');
                                    ConnectionHandler.insertEdgeBefore(connection, edge);
                                }
                            }
                        }
                    }
                },
                onCompleted: (res) => {
                    reset({message: '', pollOptions: [], media: []});
                    setShowPoll(false);
                    setShowMedia(false);
                    setIsExpanded(false);
                    onCommentCreated?.(res?.createVibePost?.id);
                }
            });
        } catch (error) {
            console.error('Error creating comment:', error);
        }
    });

    const handleMinimalClick = React.useCallback(() => {
        if (!isAuthorized) {
            onShowLoginPrompt?.();
            return;
        }
        setIsExpanded(true);
        setTimeout(() => {
            setFocus('message');
        }, 0);
    }, [setFocus, isAuthorized, onShowLoginPrompt]);

    const handleTextareaFocus = React.useCallback(() => {
        setIsExpanded(true);
    }, []);

    React.useEffect(() => {
        if (messageLength > 0 && isAuthorized) {
            setIsExpanded(true);
        }
    }, [messageLength, isAuthorized]);

    const handleKeyDown = React.useCallback((event) => {
        // Cmd+Enter on Mac, Ctrl+Enter on Windows/Linux
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            if (!isCreatingComment) {
                onSubmit();
            }
        }
    }, [isCreatingComment, onSubmit]);

    const handleCancel = React.useCallback(() => {
        reset({message: '', pollOptions: [], media: []});
        setShowPoll(false);
        setShowMedia(false);
        setIsExpanded(false);
    }, [reset]);

    const isSaving = isCreatingComment || isCreatingPoll;

    const handleAddPoll = React.useCallback(() => {
        if (!showPoll) {
            setShowPoll(true);
            setValue('pollOptions', [{value: ''}, {value: ''}], {shouldValidate: false, shouldDirty: true});
        }
    }, [showPoll, setValue]);

    const handleRemovePoll = React.useCallback(() => {
        setShowPoll(false);
        setValue('pollOptions', [], {shouldValidate: false, shouldDirty: false});
    }, [setValue]);

    const handleAddMedia = React.useCallback(() => {
        if (!showMedia) {
            setShowMedia(true);
            setValue('media', [{uri: ''}], {shouldValidate: false, shouldDirty: true});
        }
    }, [showMedia, setValue]);

    const handleRemoveMedia = React.useCallback(() => {
        setShowMedia(false);
        setValue('media', [], {shouldValidate: false, shouldDirty: false});
    }, [setValue]);

    if (!isExpanded) {
        return (
            <div>
                <div 
                    className='background-color-white padding-1dot5rem border-radius-0dot75rem shadow-light-md cursor-pointer border-style-solid border-width-0dot0625rem border-color-gray-300'
                    onClick={handleMinimalClick}
                >
                    <div className='text-md color-gray-500'>
                        <FormattedMessage defaultMessage='Write a comment...' />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className={classNames('background-color-white padding-1dot5rem border-radius-0dot75rem shadow-light-md border-style-solid border-width-0dot0625rem border-color-gray-300', {
                'opacity-0dot6 pointer-events-none': isSaving
            })}>
                <form onSubmit={onSubmit}>
                    <div className='display-flex justify-content-flex-end'>
                        <button
                            type='button'
                            aria-label='Close'
                            onClick={handleCancel}
                            disabled={isSaving}
                            className={classNames('background-color-transparent border-style-none padding-0 cursor-pointer', {
                                'opacity-0dot5 cursor-not-allowed': isSaving
                            })}
                        >
                            <XIcon className='display-block height-1dot5rem width-1dot5rem color-gray-500' />
                        </button>
                    </div>
                    <div className='padding-top-0dot75rem'>
                        <textarea
                            {...register('message', {
                                required: 'Comment message is required',
                                maxLength: {
                                    value: maxLength,
                                    message: `Comment cannot exceed ${maxLength} characters`
                                },
                                validate: (value) => {
                                    const trimmed = value?.trim() || '';
                                    if (!trimmed) {
                                        return 'Comment message cannot be empty';
                                    }
                                    return true;
                                }
                            })}
                            className='width-100percent padding-0dot75rem border-style-none text-md color-gray-900'
                            style={{resize: 'none', outline: 'none'}}
                            rows={3}
                            placeholder={intl.formatMessage({defaultMessage: 'Write a comment...'})}
                            maxLength={maxLength}
                            onKeyDown={handleKeyDown}
                            onFocus={handleTextareaFocus}
                        />
                    </div>
                    {!showPoll && !showMedia && (
                        <div className='padding-top-1rem display-flex column-gap-0dot75rem'>
                            <Button
                                type='button'
                                size='sm'
                                color='primary'
                                plain={true}
                                clickHandler={handleAddPoll}
                                disabled={isCreatingComment || isCreatingPoll}
                            >
                                <div className='display-flex align-items-center column-gap-0dot5rem'>
                                    <BarChartSquare01Icon className='display-block width-1rem height-1rem'/>
                                    <span>
                                        <FormattedMessage defaultMessage='Add poll' />
                                    </span>
                                </div>
                            </Button>
                            <Button
                                type='button'
                                size='sm'
                                color='primary'
                                plain={true}
                                clickHandler={handleAddMedia}
                                disabled={isCreatingComment || isCreatingPoll}
                            >
                                <div className='display-flex align-items-center column-gap-0dot5rem'>
                                    <Image03Icon className='display-block width-1rem height-1rem'/>
                                    <span>
                                        <FormattedMessage defaultMessage='Add media' />
                                    </span>
                                </div>
                            </Button>
                        </div>
                    )}
                    {showPoll && (
                        <PollForm
                            register={register}
                            formState={formState}
                            control={control}
                            onRemove={handleRemovePoll}
                        />
                    )}
                    {showMedia && (
                        <MediaForm
                            control={control}
                            setValue={setValue}
                            formState={formState}
                            onRemove={handleRemoveMedia}
                        />
                    )}
                    <div className='padding-bottom-0dot75rem'>
                        {formState.errors.message && (
                            <p className='text-sm color-error-600 padding-top-0dot5rem'>{formState.errors.message.message}</p>
                        )}
                        <div className='display-flex align-items-center justify-content-space-between padding-top-1rem'>
                            <div className='text-sm color-gray-500'>
                                {messageLength}/{maxLength}
                            </div>
                            <div className='display-flex align-items-center column-gap-0dot75rem'>
                            <Button
                                type='submit'
                                size='md'
                                color='primary-gradient'
                                isLoading={isCreatingComment || isCreatingPoll}
                                disabled={!message?.trim() || messageLength > maxLength}
                                >
                                <FormattedMessage defaultMessage='Comment' />
                            </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default CreateCommentForm;

